import type { EntityReferenceId } from "../../../app/types.js";
import { numberFormat, parseLocaleNumber, reformatNumberText, type TypedIn } from "../../../app/locale.js";

/**
 * What a List Sheet cell holds, and how its number is read back.
 *
 * Lifted out of the component so the part that can silently corrupt money is
 * testable without a DOM (codex review asked for exactly the sequence these
 * tests now cover). The rule the whole file exists to enforce:
 *
 *   **A numeric cell is identified by the PAIR (text, locale), never by text
 *   alone.** "1,234" is 1234 in English and 1.234 in Spanish. Two identical
 *   strings written in different languages are different numbers, and the same
 *   number is different strings — so every "has this changed?" comparison here
 *   compares pairs.
 */

export type ListColumnKey = "name" | "quantity" | "unit" | "size" | "price" | "link" | "section" | "notes";

export type Cells = Partial<Record<ListColumnKey, string>>;

export type ListItemPayload = {
  name: string;
  quantity: number | null;
  unit: string;
  size: string;
  productUrl: string;
  expectedPriceMinor: number | null;
  priceCurrency: string;
  storeSection: string;
  notes: string;
  productId: EntityReferenceId | null;
};

/**
 * The locale a cell's text was TYPED in, per numeric column.
 *
 * Read back with this rather than with whatever the app has switched to since,
 * because that is what the person meant. A language change arrives from their
 * other devices without them doing anything, and the dangerous strings are the
 * ones both languages accept: an English "1,234" is read by Spanish as 1.234,
 * quietly, so a price becomes 123 cents.
 *
 * Per COLUMN, not per row: a row is not typed all at once, so any edit made
 * after a language change leaves two cells written in different languages.
 */
export type NumericColumn = "quantity" | "price";

/**
 * Every column whose text is INTERPRETED rather than stored as typed.
 *
 * The two numbers, and the NAME — because a bare name is offered to the shared
 * note parser, which reads inline detail out of "Olive oil 2 lb $12.99" and is
 * English-only. That gate used to ask the app what language it is in NOW, at
 * flush time, so a line typed in Spanish and flushed after the language changed
 * (offline in a shop, or just inside the 450 ms debounce) was handed to the
 * English parser: "Leche $12,34" became an item named "Leche" priced $1,234.00
 * (codex review). What matters is the language the LINE was typed in.
 */
export type StampedColumn = NumericColumn | "name";
export type TypedInByColumn = Record<StampedColumn, TypedIn>;

export const NUMERIC_COLUMNS: readonly NumericColumn[] = ["quantity", "price"];
export const STAMPED_COLUMNS: readonly StampedColumn[] = ["name", "quantity", "price"];

export function isNumericColumn(key: ListColumnKey): key is NumericColumn {
  return (NUMERIC_COLUMNS as readonly string[]).includes(key);
}

export function isStampedColumn(key: ListColumnKey): key is StampedColumn {
  return (STAMPED_COLUMNS as readonly string[]).includes(key);
}

export function stampAll(locale: TypedIn): TypedInByColumn {
  return { name: locale, quantity: locale, price: locale };
}

// This sheet's price and quantity cells are NOT `<input type="number">`. They
// are plain text inputs (`inputMode="decimal"` only hints at a keyboard), so
// the browser guarantees nothing about the decimal mark and the app owns both
// halves of the round trip.
//
// That distinction is why the previous code corrupted money rather than merely
// looking wrong: it rendered a dot decimal, and parsed by stripping every comma
// and calling Number. A Spanish user typing "12,34" — the correct way to write
// it in their language — saved 1234.00, a hundredfold error, silently. "1.234,56"
// saved 1.23. Both reproduce in a node REPL against the old two lines.
export function minorToDecimal(value?: number | null): string {
  if (value == null) return "";
  return numberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value / 100);
}

export function parseQuantity(value: string | undefined, typedIn: TypedIn): number | null {
  const parsed = parseLocaleNumber((value ?? "").trim(), typedIn);
  return parsed != null && parsed > 0 ? parsed : null;
}

// `parseLocaleNumber` REJECTS what it cannot read rather than guessing, which is
// the whole point here: "1.234" is one-point-two-three-four in English and one
// thousand two hundred thirty four in Spanish, and no amount of cleverness
// recovers which was meant. A rejection leaves the cell unchanged and visible; a
// guess writes a plausible wrong number into a money column and says nothing.
export function parsePriceMinor(value: string | undefined, typedIn: TypedIn): number | null {
  const parsed = parseLocaleNumber((value ?? "").trim(), typedIn);
  return parsed != null && parsed >= 0 ? Math.round(parsed * 100) : null;
}

/** How each numeric column is written. Price is money — always two decimals;
 * quantity is a count or a weight, so it keeps whatever precision it has. */
const COLUMN_FORMAT: Record<NumericColumn, Intl.NumberFormatOptions> = {
  price: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  quantity: { maximumFractionDigits: 20, useGrouping: false }
};

function formatFor(column: NumericColumn, value: number, locale: TypedIn): string {
  // Price values are MINOR units everywhere in this file.
  const amount = column === "price" ? value / 100 : value;
  return new Intl.NumberFormat(locale, COLUMN_FORMAT[column]).format(amount);
}

/**
 * What the SERVER holds for one numeric cell, and the text it was rendered as.
 *
 * A cell whose text is still exactly what we seeded has not been edited, so its
 * number is known exactly and must not be re-derived from the string. Deriving
 * it makes a value the user never looked at depend on two fragile things: that
 * the formatter round-trips perfectly, and that the language is still the one it
 * was seeded in. Neither holds. This stack propagates a language change to a
 * user's other devices, so an English "1.5" sitting in a mounted row can become
 * unparseable under es while nobody is touching it — and the flush that follows
 * an unrelated edit would then send quantity: null and erase it.
 */
export type SeededNumber = {
  /** The text the server's number was rendered as at seed time. */
  text: string;
  /** The number the server holds — minor units for price. */
  value: number | null;
  /**
   * The locale `text` is written in, WITHOUT which the text does not identify
   * the number (codex review).
   *
   * A seed of "1,234" written in English is 1234. A Spanish speaker typing the
   * same four characters means 1.234 — a different number, spelled the same.
   * Comparing text alone read their edit as "unchanged" and sent the old value
   * back, so the change they made silently never happened.
   */
  typedIn: TypedIn;
};

export type SeededNumbers = Record<NumericColumn, SeededNumber>;

/** The seed for a row the server has never seen. Its texts are empty, which
 * means the same thing in both languages. */
export function emptySeed(locale: TypedIn): SeededNumbers {
  return { quantity: { text: "", value: null, typedIn: locale }, price: { text: "", value: null, typedIn: locale } };
}

/**
 * A seed cell, with its text DERIVED from its value rather than copied off the
 * row.
 *
 * The invariant a seed exists to carry is "this text renders this number", and
 * copying the row's text broke it whenever the two disagreed. The dangerous
 * case: a flush that fires mid-token sends the seeded value for text it cannot
 * read (correctly — that is the non-destructive rule below), and then seeded
 * `{ text: "1.2", value: 2 }`. The rewrite trusts that pairing, so a language
 * change would replace a half-typed "1.2" with "2" UNDER THE CARET, and the
 * rest of the number the user was typing landed on the wrong stem: they typed
 * 1.234 and the server was sent 234.
 *
 * Deriving the text makes the pairing true by construction, in both directions:
 * an empty cell can never hold a number (which had made a note-parsed value
 * unclearable), and unreadable text can never claim to be one.
 */
function seedCell(column: NumericColumn, value: number | null, locale: TypedIn): SeededNumber {
  return { text: value == null ? "" : formatFor(column, value, locale), value, typedIn: locale };
}

/**
 * The seed a row should carry once the server has accepted a payload.
 *
 * A created or pasted row starts with no seed, which is right while it is only
 * local. The moment it is SAVED the server holds a number for it, and leaving
 * the seed empty would keep that row's text as its source of truth for the rest
 * of the session — so a language change after the save could still erase an
 * untouched value on a later edit, which is exactly the bug this seed exists to
 * prevent, one step further along.
 */
export function seedFromPayload(payload: ListItemPayload, locale: TypedIn): SeededNumbers {
  // ONE locale, and it is the one this text is being rendered in right now —
  // not the row's stamps. The old signature took both the row's text and the
  // row's stamps, which could disagree with each other across a language change
  // that landed while the write was in the air: text formatted at t2 labelled
  // with a language from t0.
  return {
    quantity: seedCell("quantity", payload.quantity ?? null, locale),
    price: seedCell("price", payload.expectedPriceMinor ?? null, locale)
  };
}

/**
 * An untouched numeric cell keeps the number the server sent; only a cell whose
 * text has actually changed is parsed. See {@link SeededNumber}.
 */
export function seededOr(
  text: string | undefined,
  seed: SeededNumber,
  typedIn: TypedIn,
  parse: (value: string | undefined) => number | null
): number | null {
  // BOTH halves, or an edit disappears: the same string under a different
  // language is a different number, and answering with the seeded value would
  // send back what the server already had.
  if ((text ?? "") === seed.text && typedIn === seed.typedIn) return seed.value;

  const parsed = parse(text);
  if (parsed != null) return parsed;

  // An EMPTY cell means "no value" — that is how someone clears a price, and it
  // must go through as null. Text that is merely unreadable must not: this
  // sheet has no error UI at all, so sending null for it silently DELETED the
  // household's price while the cell went on displaying the number (codex
  // review). Keeping the server's value is the non-destructive half of the
  // rejection this parser promises.
  return (text ?? "").trim() ? seed.value : null;
}



/**
 * Rewrites a row's numbers in a new language, keeping every meaning intact.
 *
 * Called when the app's language changes under a mounted sheet, which happens
 * on its own — the preference syncs from the user's other devices. Before this,
 * nothing rewrote the text: a Spanish session went on showing English numbers,
 * and the first keystroke into such a cell re-labelled the whole old string as
 * Spanish, writing $1.23 over $1,234.56 (codex review).
 *
 * A cell that cannot be read — a half-typed "1," — is left exactly as it is,
 * text and stamp together, which is still correct: the stamp says what language
 * it is in and it will be read back that way.
 */
export function reformatNumbers(
  cells: Cells,
  seeded: SeededNumbers,
  typedIn: TypedInByColumn,
  to: TypedIn,
  /** Cells the caret is in. Their text, stamp and caret are left alone — see
   * {@link useCaretCell}. */
  isComposing: (column: NumericColumn) => boolean = () => false
): { cells: Cells; seeded: SeededNumbers; typedIn: TypedInByColumn } {
  const nextCells: Cells = { ...cells };
  const nextSeeded = { ...seeded };
  const nextTypedIn = { ...typedIn };

  for (const column of NUMERIC_COLUMNS) {
    const seed = seeded[column];
    const text = cells[column] ?? "";
    // The seed still catches up: it is not on screen, and its value is the
    // server's, so re-rendering it costs the user nothing.
    if (isComposing(column)) {
      nextSeeded[column] = { ...seed, text: seed.value == null ? "" : formatFor(column, seed.value, to), typedIn: to };
      continue;
    }
    // Untouched is the pair test, same as everywhere else in this file.
    const untouched = text === seed.text && typedIn[column] === seed.typedIn;
    // The seed holds the server's exact number, so its text is re-rendered
    // rather than re-parsed — no round trip, nothing to lose.
    const seedText = seed.value == null ? "" : formatFor(column, seed.value, to);
    nextSeeded[column] = { ...seed, text: seedText, typedIn: to };

    if (untouched) {
      nextCells[column] = seedText;
      nextTypedIn[column] = to;
      continue;
    }

    const rewritten = reformatNumberText(text, typedIn[column], to, COLUMN_FORMAT[column]);
    if (rewritten == null) continue;
    nextCells[column] = rewritten;
    nextTypedIn[column] = to;
  }

  return { cells: nextCells, seeded: nextSeeded, typedIn: nextTypedIn };
}
